import type { ListenerEffectAPI, TypedStartListening } from '@reduxjs/toolkit';
import { isEqual, keys } from 'lodash-es';
import type { KeyedState } from './keyedReducer';
import type { AppExtraArgument, AppThunkDispatch } from './types';

interface KeyedListenerParams<StoreState, InstanceState> {
  /** Point to the key of your KeyedState. This is the state space we monitor for changes */
  getKeyedState: (state: StoreState) => KeyedState<InstanceState>;
  /**
   * When a new state instance is noticed, this function will be called with the instanceID of that new instance.
   * Expects you to return the cleanup function of the listeners you create. When the state instance is destroyed,
   * your cleanup function will be called.
   */
  startInstanceListeners: (
    listener: ListenerEffectAPI<StoreState, AppThunkDispatch, AppExtraArgument>,
    instanceID: string
  ) => () => void;
}

/**
 * A "higher-order" listener which will listen to changes in your KeyedState, and upon recognizing that either a state instance
 * is either created or destroyed, will spin up or spin down listeners related to that state instance.
 */
export function setupKeyedListener<StoreState, InstanceState>(
  startListening: TypedStartListening<StoreState, AppThunkDispatch, AppExtraArgument>, // Partially generalized state here for testing purposes ("StoreState")
  params: KeyedListenerParams<StoreState, InstanceState>
) {
  const currentListeners = new Map<string, () => void>();
  const unsubscribeKeyedListenerWatcher = startListening({
    predicate: (_, next, prev) => {
      return !isEqual(Object.keys(params.getKeyedState(prev)), Object.keys(params.getKeyedState(next)));
    },
    effect: async (_, listener) => {
      // There has been a change in state instances present. We need to change the active listeners to get back in line.
      const { instancesNeedingListeners, listenersNoLongerNeeded } = getActiveListenerChangesNeeded(
        currentListeners,
        params.getKeyedState(listener.getState())
      );

      instancesNeedingListeners.forEach(id => {
        const cleanupFn = params.startInstanceListeners(listener, id);
        currentListeners.set(id, cleanupFn);
      });

      listenersNoLongerNeeded.forEach(id => {
        const cleanupFn = currentListeners.get(id);
        cleanupFn?.();
        currentListeners.delete(id);
      });
    },
  });

  return () => {
    unsubscribeKeyedListenerWatcher();
  };
}

/**
 * Compares the latest state with the current tracked listeners and determines what we need to do to get back to
 * one listener instance per state instance
 */
function getActiveListenerChangesNeeded(currentListeners: Map<string, () => void>, keyedState: KeyedState<unknown>) {
  const currentStateInstances = keys(keyedState);
  const currentStateInstancesSet = new Set(currentStateInstances);

  const instancesNeedingListeners = currentStateInstances.filter(instanceID => !currentListeners.has(instanceID));

  const listenersNoLongerNeeded = [...currentListeners.keys()].filter(
    listenerID => !currentStateInstancesSet.has(listenerID)
  );

  return { instancesNeedingListeners, listenersNoLongerNeeded };
}
